<script lang="ts">
  /* OrderList — incoming/outgoing orders with stage, date-range, counterparty
     and free-text filters. Sellers can also record an order on behalf of a
     client (phone orders) from here. */
  import DateInput from "$components/DateInput.svelte";
  import { onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import { _, locale } from "svelte-i18n";
  import AppShell from "$components/AppShell.svelte";
  import SearchSelect from "$components/SearchSelect.svelte";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import SectionGuide from "$components/SectionGuide.svelte";
  import IconAction from "$components/IconAction.svelte";
  import { canAction } from "$lib/permissions";
  import { auth } from "$lib/auth";
  import { hasSurface } from "$lib/surfaces";
  import { calendarIsoDate, companyCalendarDate, formatDayFirst, formatEur } from "$lib/format";
  import {
    fetchOrders,
    deleteOrders,
    restoreOrders,
    fetchOrderCounterparties,
    fetchOrderClients,
    fetchCartDrafts,
    clearCart,
    STAGE_LABEL_KEYS,
    STAGE_PHASES,
    PHASE_LABEL_KEYS,
    PHASE_ORDER,
    type OrderListItem,
    type Counterparty,
    type CartDraft,
    type Stage,
  } from "$lib/orders";
  import { fetchSuppliers } from "$lib/suppliers";
  import { subscribe, type RealtimeListener } from "$lib/realtime";
  import { prefKey, readPref, writePref } from "$lib/prefs";
  import { columnWidths } from "$lib/columnWidths.svelte";

  // Column widths the user dragged in the header, remembered in this browser.
  const colWidths = columnWidths("orders");

  let { params }: { params: { slug: string } } = $props();
  const slug = $derived(params?.slug ?? "");
  const moneyVisible = $derived(
    $auth.memberships.find((m) => m.tenant.slug === slug)?.permission_version !== 2 ||
    canAction($auth, slug, "purchase_prices.view") || canAction($auth, slug, "sales_prices.view"),
  );
  const tenantId = $derived(
    $auth.memberships.find((m) => m.tenant.slug === slug)?.tenant.id,
  );
  /* Orders still being composed. Not a strip of their own above the table any
     more: a draft is a row like every other row, and Draft is the status it is
     in. It is an outgoing order — we are the one placing it — so it is listed
     with the outgoing ones, and the Outgoing tab carries a count of the unsent
     ones so they are never only findable by remembering they exist. */
  let drafts = $state<CartDraft[]>([]);

  // Stage filter options: i18n keys instead of literal labels. The intermediate
  // stages reuse the full stage labels; the short ".tab." labels are kept for
  // the long-standing milestone stages.
  //
  // These render as a dropdown, not a tab strip. Sixteen stages wrapped to nine
  // rows on a phone and pushed the order table below the fold — the whole screen
  // was filter. "All" is the dropdown's `allLabel`, so it is not listed here.
  const STAGES: { value: string; labelKey: string }[] = [
    // Not a Stage on the server: an unsent order has not entered the machine
    // yet. It is in the list because the list is of orders, and it is one.
    { value: "draft",                        labelKey: "orders.stage.draft" },
    { value: "open",                         labelKey: "orders.stage.open" },
    { value: "pending_confirmation",         labelKey: "orders.stage.pendingConfirmation" },
    { value: "confirmed",                    labelKey: "orders.stage.confirmed" },
    { value: "sent_to_picking",              labelKey: "orders.stage.sentToPicking" },
    { value: "needs_correction",             labelKey: "orders.stage.needsCorrection" },
    { value: "picked",                       labelKey: "orders.stage.picked" },
    { value: "documents_in_preparation",     labelKey: "orders.stage.documentsInPreparation" },
    { value: "documents_prepared",           labelKey: "orders.stage.documentsPrepared" },
    { value: "transport_requested",          labelKey: "orders.stage.transportRequested" },
    { value: "transport_confirmed",          labelKey: "orders.stage.transportConfirmed" },
    { value: "transport_label_received",     labelKey: "orders.stage.transportLabelReceived" },
    { value: "dispatched",                   labelKey: "orders.stage.dispatched" },
    { value: "in_transit",                   labelKey: "orders.stage.inTransit" },
    { value: "goods_received",               labelKey: "orders.stage.goodsReceived" },
    { value: "documents_need_correction",    labelKey: "orders.stage.documentsNeedCorrection" },
    { value: "documents_confirmed",          labelKey: "orders.stage.documentsConfirmed" },
    { value: "documents_sent_to_accounting", labelKey: "orders.stage.documentsSentToAccounting" },
    { value: "cancelled",                    labelKey: "orders.stage.cancelled" },
  ];

  // Stages grouped by their commercial phase (OE-A11), so the filter reads
  // as six phases instead of a flat wall of 18 machine states.
  /** «Удалённые» in the status filter — not a stage (2026-09-24). */
  const DELETED = "deleted";
  const stageOptions = $derived([
    ...STAGES.map((s) => {
      const phase =
        s.value === "draft"
          ? "draft"
          : (STAGE_PHASES[s.value as Stage] ?? "draft");
      return { value: s.value, label: $_(s.labelKey), group: $_(PHASE_LABEL_KEYS[phase]) };
    }),
    // Not a stage: the orders this company took off its list (2026-09-24).
    { value: DELETED, label: $_("orders.stage.deleted"), group: $_(PHASE_LABEL_KEYS.cancelled) },
  ]);
  const phaseRank = $derived.by(() => {
    const rank = new Map<string, number>();
    PHASE_ORDER.forEach((p, i) => rank.set($_(PHASE_LABEL_KEYS[p]), i));
    rank.set($_(PHASE_LABEL_KEYS.cancelled), PHASE_ORDER.length);
    return (g: string) => rank.get(g) ?? PHASE_ORDER.length + 1;
  });

  // Default direction follows the tenant's active link directions (audit
  // OE-A08): a pure buyer lands on Outgoing (its own purchases), a seller on
  // Incoming; ties break by company-type terminology server-side.
  /* Role adaptation (see $lib/surfaces): a shop is the end of the chain — it
     places orders and never fulfils one, so the Incoming side and the agent
     flow are not part of its product at all. A company that does have clients
     keeps both whatever its type says. */
  const showIncoming = $derived(hasSurface($auth, slug, "orders.incoming") && (canAction($auth,slug,"sales_orders.view",["orders","view"]) || canAction($auth,slug,"fulfillment.view",["orders","view"])));
  /* The same two questions for the Outgoing side. Whether a manufacturer that
     buys nothing gets it is the server's call (surfaces: a buy link forces it
     on, an operator can pin it); a storekeeper with only receipts.view sees
     our purchase orders from dispatch on, as the order scope allows. */
  const allowOutgoing = $derived(hasSurface($auth, slug, "orders.outgoing") && (canAction($auth,slug,"purchase_orders.view",["orders","view"]) || canAction($auth,slug,"receipts.view")));
  const showAgent = $derived(hasSurface($auth, slug, "orders.agent") && canAction($auth,slug,"sales_orders.edit",["orders","edit"]));
  const DEFAULT_DIRECTION: "incoming" | "outgoing" =
    ($auth.memberships.find((m) => m.tenant.slug === slug)?.capabilities?.defaults
      ?.orders_direction as "incoming" | "outgoing") ?? "incoming";
  const DEFAULT_PERIOD = "month";

  // Period filter. Current Month is the default; "Custom range" is a separate
  // toggle (not a dropdown entry) that reveals two date inputs.
  const PERIOD_OPTIONS: { value: string; labelKey: string }[] = [
    { value: "month",        labelKey: "orders.period.month" },
    { value: "today",        labelKey: "orders.period.today" },
    { value: "yesterday",    labelKey: "orders.period.yesterday" },
    { value: "this_week",    labelKey: "orders.period.thisWeek" },
    { value: "last_week",    labelKey: "orders.period.lastWeek" },
    { value: "prev_month",   labelKey: "orders.period.prevMonth" },
    { value: "this_quarter", labelKey: "orders.period.thisQuarter" },
    { value: "prev_quarter", labelKey: "orders.period.prevQuarter" },
    { value: "this_year",    labelKey: "orders.period.thisYear" },
    { value: "last_year",    labelKey: "orders.period.lastYear" },
  ];

  let activePeriod = $state(DEFAULT_PERIOD);
  let customOpen = $state(false);
  let customFrom = $state("");
  let customTo = $state("");

  function monthRange(y: number, m: number) {
    return {
      from: calendarIsoDate(new Date(Date.UTC(y, m, 1))),
      to: calendarIsoDate(new Date(Date.UTC(y, m + 1, 0))),
    };
  }
  function weekRange(base: Date, offsetWeeks: number) {
    const d = new Date(base);
    const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
    d.setUTCDate(d.getUTCDate() - dow + offsetWeeks * 7);
    const end = new Date(d);
    end.setUTCDate(end.getUTCDate() + 6);
    return { from: calendarIsoDate(d), to: calendarIsoDate(end) };
  }
  function quarterRange(base: Date, offset: number) {
    const q = Math.floor(base.getUTCMonth() / 3) + offset;
    const y = base.getUTCFullYear() + Math.floor(q / 4);
    const qq = ((q % 4) + 4) % 4;
    const startMonth = qq * 3;
    return {
      from: calendarIsoDate(new Date(Date.UTC(y, startMonth, 1))),
      to: calendarIsoDate(new Date(Date.UTC(y, startMonth + 3, 0))),
    };
  }

  /** Resolve the active period to a {from,to} ISO date range (or empty). */
  const resolvedRange = $derived.by((): { from?: string; to?: string } => {
    if (activePeriod === "custom") {
      if (customFrom && customTo) return { from: customFrom, to: customTo };
      return {};
    }
    const now = companyCalendarDate(slug);
    switch (activePeriod) {
      case "today": { const t = calendarIsoDate(now); return { from: t, to: t }; }
      case "yesterday": { const d = new Date(now); d.setUTCDate(d.getUTCDate() - 1); const t = calendarIsoDate(d); return { from: t, to: t }; }
      case "this_week": return weekRange(now, 0);
      case "last_week": return weekRange(now, -1);
      case "month": return monthRange(now.getUTCFullYear(), now.getUTCMonth());
      case "prev_month": { const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)); return monthRange(d.getUTCFullYear(), d.getUTCMonth()); }
      case "this_quarter": return quarterRange(now, 0);
      case "prev_quarter": return quarterRange(now, -1);
      case "this_year": return { from: `${now.getUTCFullYear()}-01-01`, to: `${now.getUTCFullYear()}-12-31` };
      case "last_year": { const y = now.getUTCFullYear() - 1; return { from: `${y}-01-01`, to: `${y}-12-31` }; }
      default: return {};
    }
  });

  function fmtRangeDate(iso?: string): string {
    if (!iso) return "-";
    return formatDayFirst(new Date(iso + "T00:00:00"), { day: "2-digit", month: "short", year: "numeric" }, $locale);
  }
  const activeRangeLabel = $derived(
    resolvedRange.from
      ? `${fmtRangeDate(resolvedRange.from)} – ${fmtRangeDate(resolvedRange.to)}`
      : $_("orders.period.allDates"),
  );

  const DIRECTION_LABEL_KEYS: Record<string, string> = {
    incoming:  "orders.dir.label.incoming",
    outgoing:  "orders.dir.label.outgoing",
    internal:  "orders.dir.label.internal",
    unrelated: "orders.dir.label.unrelated",
  };

  let orders = $state<OrderListItem[]>([]);
  let total = $state(0);


  let loading = $state(true);
  let page = $state(1);
  let activeStage = $state("");
  let activeDirection = $state<string>(DEFAULT_DIRECTION);
  let searchQ = $state("");

  let counterparties = $state<Counterparty[]>([]);
  let activeCounterparty = $state("");
  const counterpartyOptions = $derived(
    counterparties.map((c) => ({ value: c.slug, label: c.name })),
  );
  /* Incoming orders come from our clients, outgoing ones go to our suppliers.
     The filter says which of the two lists it is showing, and the list itself
     is fetched for that side only. */
  const counterpartyLabelKey = $derived(
    activeDirection === "outgoing" ? "orders.filter.supplier" : "orders.filter.client",
  );
  const counterpartyAllKey = $derived(
    activeDirection === "outgoing" ? "orders.filter.allSuppliers" : "orders.filter.allClients",
  );
  /* The table column says who the other side actually is — «Клиент» on the
     incoming side, «Поставщик» on the outgoing one. «Контрагент» only remains
     for the mixed (unfiltered) view, where the rows disagree. */
  const counterpartyColKey = $derived(
    activeDirection === "incoming"
      ? "orders.filter.client"
      : activeDirection === "outgoing"
        ? "orders.filter.supplier"
        : "orders.col.counterparty",
  );

  /* An order is placed with a supplier, so without one there is nothing to
     compose. The button is hidden rather than disabled — a dead button on the
     header of an empty page explains nothing. */
  let supplierCount = $state(0);
  const canCreateOrder = $derived(supplierCount > 0 && canAction($auth,slug,"purchase_orders.edit",["orders","edit"]));
  /* Same reasoning for the agent flow: an order is written FOR a client, so
     with no clients there is nothing to write. Hidden, not disabled. */
  let clientCount = $state(0);
  const canOrderForClient = $derived(showAgent && clientCount > 0);

  function composeOrder() {
    push(`/t/${slug}/orders/new`);
  }

  function orderForClient() {
    push(`/t/${slug}/orders/for-client`);
  }

  function continueDraft(d: CartDraft) {
    push(`/t/${slug}/orders/new/${encodeURIComponent(d.seller_slug)}`);
  }

  async function discardDraft(d: CartDraft) {
    if (!confirm($_("orders.compose.discard.confirm"))) return;
    try {
      await clearCart(slug, d.seller_slug);
      drafts = drafts.filter((x) => x.id !== d.id);
    } catch { /* non-fatal - it stays in the list */ }
  }

  // "Remove filters" shows whenever anything deviates from the defaults
  // (Incoming · all stages · Current Month · no counterparty · no search).
  const hasActiveFilters = $derived(
    activeDirection !== DEFAULT_DIRECTION ||
    activeStage !== "" ||
    activePeriod !== DEFAULT_PERIOD ||
    activeCounterparty !== "" ||
    searchQ.trim() !== "",
  );

  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  /* Draft rows that survive the filters on screen. A draft is an order we are
     placing, so it sits on the Outgoing side and under the Draft status, and
     the counterparty and search boxes narrow it like any other row. The date
     range does not apply: it has no placed-at date yet, which is the whole
     difference between it and the rows below it. */
  const visibleDrafts = $derived.by(() => {
    if (activeDirection !== "outgoing") return [];
    if (activeStage !== "" && activeStage !== "draft") return [];
    const q = searchQ.trim().toLowerCase();
    return drafts.filter(
      (d) =>
        (!activeCounterparty || d.seller_slug === activeCounterparty) &&
        (q.length < 2 || d.seller_name.toLowerCase().includes(q)),
    );
  });

  /* Server-side stages only. "draft" is ours, and sending it as a stage filter
     would ask the API for a stage it has never heard of. */
  const stageParam = $derived(activeStage === "draft" ? "" : activeStage);
  /** No sent orders can match while the status filter is Draft. */
  const suppressOrders = $derived(activeStage === "draft");

  /* «Удалить» through ticks (owner, 2026-09-24): tick rows, then one button
     for all of them. It takes them off *this* company's list only — the
     partner keeps its copy, documents and ledgers stay, the order still
     opens — and «Удалённые» in the status filter lists them back to restore. */
  const inDeleted = $derived(activeStage === DELETED);
  const canTidy = $derived(
    canAction($auth,slug,"purchase_orders.edit",["orders","edit"]) ||
      canAction($auth,slug,"sales_orders.edit",["orders","edit"]),
  );
  let picked = $state<Set<string>>(new Set());
  let tidying = $state(false);
  let tidyNotice = $state("");
  const allPicked = $derived(orders.length > 0 && orders.every((o) => picked.has(o.code)));
  function togglePick(code: string, on: boolean) {
    const next = new Set(picked);
    if (on) next.add(code); else next.delete(code);
    picked = next;
  }
  function toggleAll(on: boolean) {
    picked = on ? new Set(orders.map((o) => o.code)) : new Set();
  }
  async function tidy() {
    const codes = [...picked];
    if (!codes.length || tidying) return;
    if (!inDeleted && !confirm($_("orders.bulk.deleteConfirm", { values: { n: codes.length } }))) return;
    tidying = true;
    try {
      const r = inDeleted ? await restoreOrders(slug, codes) : await deleteOrders(slug, codes);
      tidyNotice = $_(inDeleted ? "orders.bulk.restored" : "orders.bulk.deleted", { values: { n: r.count } });
      await load();
    } finally {
      tidying = false;
    }
  }
  async function load() {
    loading = true;
    picked = new Set();
    try {
      if (suppressOrders) {
        orders = [];
        total = 0;
        return;
      }
      const p: Record<string, string | number | undefined> = { page };
      if (stageParam) p.stage = stageParam;
      if (activeDirection) p.direction = activeDirection;
      // Free-text search only kicks in from the 2nd character.
      if (searchQ.trim().length >= 2) p.q = searchQ.trim();
      if (activeCounterparty) p.counterparty = activeCounterparty;
      const range = resolvedRange;
      if (range.from) p.date_from = range.from;
      if (range.to) p.date_to = range.to;
      const data = await fetchOrders(slug, p);
      orders = data.results;
      total = data.count;
    } finally {
      loading = false;
    }
  }

  function reload() { page = 1; load(); }

  /** Reload the counterparty list for the side we are on, dropping a selection
   *  that does not exist on that side. */
  async function loadCounterparties() {
    try {
      counterparties = await fetchOrderCounterparties(slug, activeDirection);
      if (activeCounterparty && !counterparties.some((c) => c.slug === activeCounterparty)) {
        activeCounterparty = "";
      }
    } catch { /* non-fatal */ }
  }

  function setDirection(dir: string) {
    if (activeDirection === dir) return; // re-clicking the active tab is a no-op
    activeDirection = dir;
    loadCounterparties();
    reload();
  }

  /** Picking Draft moves to the outgoing side with it: an unsent order is one
   *  we are placing, so asking for drafts on the incoming side is asking for
   *  something that cannot exist — and it showed an empty table saying so. */
  function onStage() {
    if (activeStage === "draft" && allowOutgoing && activeDirection !== "outgoing") {
      setDirection("outgoing");
      return;
    }
    reload();
  }

  function onSearchInput() {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(reload, 250);
  }

  function onPeriod(v: string) {
    activePeriod = v;
    customOpen = false;
    reload();
  }
  function toggleCustom() {
    customOpen = !customOpen;
    if (customOpen) activePeriod = "custom";
    else if (activePeriod === "custom") { activePeriod = DEFAULT_PERIOD; reload(); }
  }
  function onCustomChange() {
    if (customFrom && customTo) reload();
  }

  /** Drop the date filter entirely — "custom" with no bounds is all dates. */
  function showAllDates() {
    activePeriod = "custom";
    customOpen = false;
    customFrom = "";
    customTo = "";
    reload();
  }

  /* Every filter on this page is remembered per user and per company: coming
     back from an order, or logging out and in again, lands on the same view.
     Only "Remove filters" (or picking something else) changes it. */
  type SavedFilters = {
    direction: string; period: string; customOpen: boolean;
    customFrom: string; customTo: string; stage: string;
    counterparty: string; q: string;
  };
  let filtersRestored = $state(false);
  function restoreFilters() {
    const saved = readPref<SavedFilters>(prefKey("orders.filters.v1", $auth.user?.id, slug));
    if (saved) {
      if (saved.direction === "incoming" || saved.direction === "outgoing") activeDirection = saved.direction;
      if (saved.period === "custom" || PERIOD_OPTIONS.some((o) => o.value === saved.period)) activePeriod = saved.period;
      customOpen = !!saved.customOpen;
      customFrom = saved.customFrom ?? "";
      customTo = saved.customTo ?? "";
      activeStage = saved.stage ?? "";
      activeCounterparty = saved.counterparty ?? "";
      searchQ = saved.q ?? "";
    }
    filtersRestored = true;
  }
  $effect(() => {
    const snapshot: SavedFilters = {
      direction: activeDirection, period: activePeriod, customOpen,
      customFrom, customTo, stage: activeStage,
      counterparty: activeCounterparty, q: searchQ,
    };
    if (!filtersRestored) return;
    writePref(prefKey("orders.filters.v1", $auth.user?.id, slug), snapshot);
  });

  function removeFilters() {
    const dirChanged = activeDirection !== DEFAULT_DIRECTION;
    activeDirection = DEFAULT_DIRECTION;
    activeStage = "";
    activePeriod = DEFAULT_PERIOD;
    activeCounterparty = "";
    searchQ = "";
    customOpen = false;
    customFrom = "";
    customTo = "";
    if (dirChanged) loadCounterparties();
    reload();
  }

  function viewOrder(code: string) {
    push(`/t/${slug}/orders/${code}`);
  }

  /** A draft has no placed-at date, so the column shows when it was last
   *  worked on — in the same YYYY-MM-DD HH:MM shape as the rows below it,
   *  and in the reader's own timezone rather than raw UTC off the wire. */
  function draftStamp(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  async function loadMeta() {
    await loadCounterparties();
    try {
      supplierCount = canAction($auth,slug,"suppliers.view",["link_requests","view"]) ? (await fetchSuppliers(slug)).length : 0;
    } catch { /* non-fatal */ }
    try {
      clientCount = showAgent ? (await fetchOrderClients(slug)).length : 0;
    } catch { /* non-fatal - the button simply stays hidden */ }
    try {
      drafts = canAction($auth,slug,"purchase_orders.view",["orders","view"]) ? await fetchCartDrafts(slug) : [];
    } catch { /* non-fatal */ }
  }

  onMount(() => {
    if (!slug) return;
    restoreFilters();
    if (activeDirection === "outgoing" && !allowOutgoing) activeDirection = "incoming";
    /* The server already flips the default away from a direction this company
       does not have; this is for a session cached before it did — landing on
       a tab that is not drawn shows an empty table and no way back. */
    if (activeDirection === "incoming" && !showIncoming) activeDirection = "outgoing";
    load();
    loadMeta();
    let disposed = false;
    let realtime: RealtimeListener | null = null;
    let reloadTimer: ReturnType<typeof setTimeout> | null = null;
    if (tenantId) {
      subscribe(`tenant:${tenantId}:dashboard`, (event) => {
        if (!event.type.startsWith("order.")) return;
        if (reloadTimer) clearTimeout(reloadTimer);
        reloadTimer = setTimeout(() => {
          load();
          loadMeta();
        }, 100);
      }).then((listener) => {
        realtime = listener;
        if (disposed) listener.unsubscribe();
      });
    }
    return () => {
      disposed = true;
      if (reloadTimer) clearTimeout(reloadTimer);
      realtime?.unsubscribe();
    };
  });
</script>

<!-- The drag handle on a caption's right edge (global .col-grip); a double
     click gives the column back its automatic width. -->
{#snippet grip(key: string)}
  <span
    class="col-grip"
    aria-hidden="true"
    use:colWidths.grip={key}
  ></span>
{/snippet}

<AppShell tenantSlug={slug} active="orders">
  <!-- Laid out like Приход and Расход (2026-09-18: «раздел заказы главное поле
       в таком же виде как приход и расход»): the selection on the left, the
       list on the right in the ledger's table. What is filtered and how is
       unchanged; only where it sits. -->
  <div class="ol-page">
    <div class="page-head">
      <div>
        <div class="title-row">
          <h1 class="page-title">{$_("orders.title")}</h1>
          <SectionGuide id="orders" />
        </div>
        <div class="head-sub">
          {#if total + visibleDrafts.length > 0}
            {$_("orders.totalCount", {
              values: {
                first: total === 0 ? 1 : ((page - 1) * 24) + 1,
                last: Math.min(page * 24, total) + visibleDrafts.length,
                total: total + visibleDrafts.length,
              },
            })}
          {:else}
            {$_("orders.noFound")}
          {/if}
        </div>
      </div>
      <div class="head-actions">
        {#if canOrderForClient}
          <!-- «Заказ за клиента» (2026-08-23): we never hand an agent the
               client's account. The seller records the order in their own
               profile, and publishing it is what makes the client see it. -->
          <button class="tool-btn" onclick={orderForClient}>
            <span class="material-icons-outlined">support_agent</span>
            {$_("orders.create.title")}
          </button>
          <InfoTooltip text={$_("orders.create.buttonHint")} label={$_("orders.create.title")} />
        {/if}
        {#if canCreateOrder}
          <!-- The tooltip sits NEXT to the button, not inside it: a button in
               a button is invalid HTML, and the tooltip click used to bubble
               into composeOrder and navigate away (audit OE-A25). -->
          <button class="btn btn--primary create-btn" onclick={composeOrder}>
            <span class="material-icons-outlined">add</span>
            {$_("orders.create.button")}
          </button>
          <InfoTooltip text={$_("orders.compose.buttonHint")} label={$_("orders.create.button")} />
        {/if}
      </div>
    </div>

    <div class="ledger-body">
      <!-- «Параметры отбора», kept to the left as on the two ledgers. Every
           control applies as it is touched; nothing to press. -->
      <aside class="filters">
        <div class="filters-title">{$_("ledger.filters.title")}</div>

        {#if showIncoming && allowOutgoing}
          <div class="preset-row" role="tablist">
            <button type="button" class="preset" class:on={activeDirection === "incoming"}
              onclick={() => setDirection("incoming")}>{$_("orders.dir.tab.incoming")}</button>
            <button type="button" class="preset" class:on={activeDirection === "outgoing"}
              onclick={() => setDirection("outgoing")}>
              {$_("orders.dir.tab.outgoing")}
              <!-- Unsent orders live on this side. Without the count they are
                   only findable by remembering they exist. -->
              {#if drafts.length > 0}<span class="dir-badge">{drafts.length}</span>{/if}
            </button>
          </div>
        {/if}

        <label class="field">
          <span>{$_("ledger.filters.period")}</span>
          <select value={activePeriod === "custom" ? "" : activePeriod}
            onchange={(e) => onPeriod((e.currentTarget as HTMLSelectElement).value)}>
            {#if activePeriod === "custom"}<option value="">{$_("orders.period.custom")}</option>{/if}
            {#each PERIOD_OPTIONS as opt}
              <option value={opt.value}>{$_(opt.labelKey)}</option>
            {/each}
          </select>
        </label>
        <button type="button" class="link-btn" onclick={toggleCustom}>
          <span class="material-icons-outlined" aria-hidden="true">date_range</span>
          {$_("orders.period.custom")}
        </button>
        {#if customOpen}
          <div class="date-row">
            <label>
              <span>{$_("orders.range.from")}</span>
              <DateInput bind:value={customFrom} onchange={() => onCustomChange()} />
            </label>
            <label>
              <span>{$_("orders.range.to")}</span>
              <DateInput bind:value={customTo} onchange={() => onCustomChange()} />
            </label>
          </div>
        {/if}
        <div class="active-range" title={$_("orders.range.active")}>
          <span class="material-icons-outlined" aria-hidden="true">event</span>
          {activeRangeLabel}
        </div>
        {#if resolvedRange.from}
          <button type="button" class="link-btn" onclick={showAllDates}>{$_("ledger.filters.allTime")}</button>
        {/if}

        <div class="field">
          <span>{$_(counterpartyLabelKey)}</span>
          <SearchSelect
            options={counterpartyOptions}
            bind:value={activeCounterparty}
            allLabel={$_(counterpartyAllKey)}
            searchPlaceholder={$_("orders.filter.counterpartySearch")}
            onchange={reload}
          />
        </div>

        <div class="field">
          <span>{$_("orders.filter.stage")}</span>
          <SearchSelect
            options={stageOptions}
            bind:value={activeStage}
            allLabel={$_("orders.stage.tab.all")}
            searchPlaceholder={$_("orders.filter.stageSearch")}
            groupRank={phaseRank}
            onchange={onStage}
          />
        </div>

        <label class="field">
          <span>{$_("orders.filter.search")}</span>
          <input type="search" placeholder={$_("orders.searchPlaceholder")}
            bind:value={searchQ} oninput={onSearchInput} />
        </label>

        {#if hasActiveFilters}
          <button type="button" class="remove-filters" onclick={removeFilters}>
            <span class="material-icons-outlined" aria-hidden="true">close</span>
            {$_("orders.filter.remove")}
          </button>
        {/if}
      </aside>

      <section class="list">
          {#if canTidy && (picked.size > 0 || tidyNotice)}
            <div class="bulk-bar">
              {#if picked.size > 0}
                <span>{$_("orders.bulk.selected", { values: { n: picked.size } })}</span>
                <button type="button" class="tool-btn" class:danger={!inDeleted} disabled={tidying} onclick={tidy}>
                  <span class="material-icons-outlined" aria-hidden="true">{inDeleted ? "restore_from_trash" : "delete_outline"}</span>
                  {$_(inDeleted ? "orders.bulk.restore" : "orders.bulk.delete")}
                </button>
              {:else}
                <span class="muted">{tidyNotice}</span>
              {/if}
            </div>
          {/if}
        {#if loading}
          <div class="loading-center">{$_("orders.loading")}</div>
        {:else if orders.length === 0 && visibleDrafts.length === 0}
          <div class="empty-state">
            <span class="material-icons-outlined" aria-hidden="true">receipt_long</span>
            <p>{$_("orders.noMatch")}</p>
            {#if resolvedRange.from}
              <!-- The period filter is on silently from the first paint, so an
                   empty table may just mean "nothing in the current month".
                   Give the reader the one click that answers which it is. -->
              <button type="button" class="tool-btn empty-btn" onclick={showAllDates}>
                <span class="material-icons-outlined" aria-hidden="true">event_busy</span>
                {$_("orders.filter.showAllDates")}
              </button>
            {/if}
          </div>
        {:else}
          <div class="table-scroll">
            <table class="ledger-table col-divided">
              <thead>
                <tr>
                  {#if canTidy}
                    <th class="col-pick">
                      <input type="checkbox" checked={allPicked} disabled={orders.length === 0}
                        title={$_("orders.bulk.selectAll")} aria-label={$_("orders.bulk.selectAll")}
                        onchange={(e) => toggleAll((e.currentTarget as HTMLInputElement).checked)} />
                    </th>
                  {/if}
                  <th class="num">{$_("orders.col.nr")}</th>
                  <th style={colWidths.style("datetime")}>{$_("orders.col.datetime")}{@render grip("datetime")}</th>
                  <th style={colWidths.style("counterparty")}>{$_(counterpartyColKey)}{@render grip("counterparty")}</th>
                  <th style={colWidths.style("orderNo")}>{$_("orders.col.orderNo")}{@render grip("orderNo")}</th>
                  <th class="num" style={colWidths.style("qty")}>{$_("orders.col.qty")}{@render grip("qty")}</th>
                  {#if moneyVisible}<th class="num" style={colWidths.style("sum")}>{$_("orders.col.sum")}{@render grip("sum")}</th>{/if}
                  <th style={colWidths.style("status")}>{$_("orders.col.status")}{@render grip("status")}</th>
                  <th class="col-act"></th>
                </tr>
              </thead>
              <tbody>
                <!-- Unsent orders first. Same row, same columns; the only thing
                     that makes them different is the status they are in. -->
                {#each visibleDrafts as d (`d${d.id}`)}
                  <tr class="clickable-row draft-tr" onclick={() => continueDraft(d)}>
                    {#if canTidy}<td class="col-pick"></td>{/if}
                    <td class="num muted">-</td>
                    <td class="nowrap">{draftStamp(d.updated_at)}</td>
                    <td>
                      <div class="strong">{d.seller_name}</div>
                      <div class="sub">{$_("orders.supplierTag")}</div>
                    </td>
                    <td class="mono">-</td>
                    <td class="num">{d.item_count}</td>
                    {#if moneyVisible}<td class="num strong">{formatEur(d.subtotal_eur)}</td>{/if}
                    <td>
                      <span class="stage-pill stage--draft">
                        <span class="stage-dot" aria-hidden="true"></span>
                        {$_("orders.stage.draft")}
                      </span>
                    </td>
                    <td class="col-act">
                      <div class="row-actions">
                        <IconAction icon="edit_note" title={$_("orders.drafts.continue")}
                          onclick={(e) => { e?.stopPropagation?.(); continueDraft(d); }} />
                        <IconAction icon="delete_outline" title={$_("orders.compose.discard")} tone="danger"
                          onclick={(e) => { e?.stopPropagation?.(); discardDraft(d); }} />
                      </div>
                    </td>
                  </tr>
                {/each}
                {#each orders as o (o.code)}
                  <tr onclick={() => viewOrder(o.code)} class="clickable-row" class:row-deleted={o.deleted}>
                    {#if canTidy}
                      <td class="col-pick" onclick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={picked.has(o.code)} aria-label={o.reference}
                          onchange={(e) => togglePick(o.code, (e.currentTarget as HTMLInputElement).checked)} />
                      </td>
                    {/if}
                    <td class="num muted">
                      {o.local_seq || "-"}
                      {#if o.downloaded_at}
                        <span class="download-icon material-icons" title="Downloaded">check_circle</span>
                      {/if}
                    </td>
                    <td class="nowrap">{o.datetime_label}</td>
                    <td>
                      {#if o.direction === "incoming"}
                        <div class="strong">{o.buyer_name_tenant}</div>
                        {#if o.buyer_name && o.buyer_name !== o.buyer_name_tenant}
                          <div class="sub">{o.buyer_name}</div>
                        {/if}
                      {:else if o.direction === "outgoing"}
                        <div class="strong">{o.seller_name}</div>
                        <div class="sub">{$_("orders.supplierTag")}</div>
                      {:else}
                        <div class="strong">{o.buyer_name || "-"}</div>
                        {#if o.buyer_email}<div class="sub">{o.buyer_email}</div>{/if}
                      {/if}
                    </td>
                    <td class="mono ref">{o.reference || "-"}</td>
                    <td class="num">{o.item_count}</td>
                    {#if moneyVisible}<td class="num strong">{formatEur(o.total_eur)}</td>{/if}
                    <td>
                      <span class="stage-pill stage--{o.stage}">
                        <span class="stage-dot" aria-hidden="true"></span>
                        {STAGE_LABEL_KEYS[o.stage] ? $_(STAGE_LABEL_KEYS[o.stage]) : o.stage}
                      </span>
                    </td>
                    <td class="col-act">
                      <div class="row-actions">
                        <IconAction icon="visibility" title={$_("orders.action.view")}
                          onclick={(e) => { e?.stopPropagation?.(); viewOrder(o.code); }} />
                        <!-- Saved but not sent (owner, 2026-09-23): the lines are
                             still ours to change — the pencil opens them. -->
                        {#if o.stage === "open" && o.direction !== "incoming" && canAction($auth,slug,"purchase_orders.edit",["orders","edit"])}
                          <IconAction icon="edit" tone="primary" title={$_("common.edit")}
                            onclick={(e) => { e?.stopPropagation?.(); push(`/t/${slug}/orders/${o.code}?edit=1`); }} />
                        {/if}
                      </div>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>

          {#if total > 24}
            <div class="pager">
              <button type="button" class="tool-btn" disabled={page <= 1}
                onclick={() => { page--; load(); }}>‹</button>
              <span>{$_("orders.pagination.pageOf", { values: { page, total: Math.ceil(total / 24) } })}</span>
              <button type="button" class="tool-btn" disabled={page >= Math.ceil(total / 24)}
                onclick={() => { page++; load(); }}>›</button>
            </div>
          {/if}
        {/if}
      </section>
    </div>
  </div>
</AppShell>

<style>
  /* The ledger's page (features/warehouse/Ledger.svelte), so the three read
     as one family. */
  .ol-page { display: flex; flex-direction: column; gap: 16px; }
  .page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .title-row { display: flex; align-items: center; gap: 8px; }
  .page-title { margin: 0; font-size: 22px; font-weight: 700; color: var(--color-surface-darker); }
  .head-sub { font-size: 13px; color: var(--color-text-muted); margin-top: 2px; }
  .head-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .create-btn { gap: 6px; }
  .create-btn .material-icons-outlined { font-size: 18px; }
  @media (max-width: 640px) {
    .page-head, .head-actions { width: 100%; }
  }

  .tool-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 7px 12px; background: #fff; border: 1px solid var(--color-divider);
    border-radius: 6px; font-family: inherit; font-size: 13px; cursor: pointer; white-space: nowrap;
    color: inherit; text-decoration: none;
  }
  .tool-btn:hover:not(:disabled) { border-color: var(--color-primary); color: var(--color-primary); }
  .tool-btn:disabled { opacity: 0.5; cursor: default; }
  .tool-btn .material-icons-outlined { font-size: 18px; }

  .ledger-body { display: grid; grid-template-columns: 250px minmax(0, 1fr); gap: 16px; align-items: start; }
  @media (max-width: 900px) { .ledger-body { grid-template-columns: 1fr; } }

  .filters {
    background: #fff; border: 1px solid var(--color-divider); border-radius: 8px;
    padding: 14px; display: flex; flex-direction: column; gap: 12px; position: sticky; top: 72px;
  }
  @media (max-width: 900px) { .filters { position: static; } }
  .filters-title {
    font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
    color: var(--color-surface-dark);
  }
  .preset-row { display: flex; border: 1px solid var(--color-divider); border-radius: 6px; overflow: hidden; }
  .preset {
    flex: 1; display: inline-flex; align-items: center; justify-content: center; gap: 5px;
    padding: 6px 4px; border: none; background: #fff; font-family: inherit; font-size: 12px;
    cursor: pointer; color: var(--color-text-muted);
  }
  .preset + .preset { border-left: 1px solid var(--color-divider); }
  .preset.on { background: var(--color-primary); color: #fff; font-weight: 600; }
  .dir-badge { background: var(--color-bg); color: var(--color-surface-darker);
    border-radius: 9px; padding: 0 6px; font-size: 11px; font-weight: 700; }
  .preset.on .dir-badge { background: rgba(255,255,255,0.25); color: #fff; }

  .field, .date-row label { display: flex; flex-direction: column; gap: 4px; font-size: 12px;
    color: var(--color-text-muted); min-width: 0; }
  .date-row label { display: contents; }
  .field input, .field select {
    padding: 6px 8px; font-family: inherit; font-size: 13px; color: var(--color-text);
    border: 1px solid var(--color-divider); border-radius: 6px; background: #fff;
    min-width: 0; width: 100%; box-sizing: border-box;
  }
  /* The shared dropdown is sized for a filter row; here it fills the panel. */
  .field :global(.ss-wrap) { width: 100%; }
  .field :global(.ss-trigger) { width: 100%; max-width: none; min-width: 0; box-sizing: border-box; }
  /* «С» and «По» one under the other, the caption beside its field: a day-first
     date and its calendar button do not fit in half of a side panel. */
  .date-row { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 6px 8px; align-items: center; }
  .link-btn {
    display: inline-flex; align-items: center; gap: 4px; align-self: flex-start;
    background: none; border: none; padding: 0; font-family: inherit;
    font-size: 12px; color: var(--color-primary); cursor: pointer; margin-top: -6px;
  }
  .link-btn .material-icons-outlined { font-size: 15px; }
  .active-range { display: flex; align-items: center; gap: 5px; font-size: 12px; color: var(--color-text-muted); }
  .active-range .material-icons-outlined { font-size: 15px; }
  .remove-filters {
    display: inline-flex; align-items: center; justify-content: center; gap: 4px; padding: 6px 12px;
    border: 1px solid #f3c9c9; background: #fdecec; color: #c62828; border-radius: 6px;
    font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer;
  }
  .remove-filters:hover { background: #fbdada; }
  .remove-filters .material-icons-outlined { font-size: 15px; }

  .list { min-width: 0; }
  .loading-center { padding: 40px; text-align: center; color: var(--color-text-muted); }
  .empty-state { padding: 48px 20px; text-align: center; color: var(--color-text-muted); background: #fff;
    border: 1px solid var(--color-divider); border-radius: 8px; }
  .empty-state .material-icons-outlined { font-size: 40px; opacity: 0.5; }
  .empty-state p { margin: 8px 0 0; font-size: 14px; }
  .empty-btn { margin-top: 14px; }
  .empty-btn .material-icons-outlined { font-size: 18px; opacity: 1; }

  .table-scroll { overflow-x: auto; background: #fff; border: 1px solid var(--color-divider); border-radius: 8px; }
  .ledger-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .ledger-table th {
    position: relative;
    text-align: left; padding: 9px 10px; font-size: 10px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-surface-dark);
    background: #eef1f4; border-bottom: 1px solid var(--color-surface-dark); white-space: nowrap;
  }
  .ledger-table td { padding: 7px 10px; border-bottom: 1px solid var(--color-divider); vertical-align: middle; }
  .ledger-table tbody tr:last-child td { border-bottom: none; }
  .clickable-row { cursor: pointer; }
  .clickable-row:hover td { background: #f7f9fb; }
  /* Figures and their captions on the right edge of the column. */
  .num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  .ledger-table th.num { text-align: right; }
  .nowrap { white-space: nowrap; }
  .mono { font-family: monospace; font-size: 12px; }
  .ref { font-weight: 700; color: var(--color-primary); }
  .muted { color: var(--color-text-muted); }
  .strong { font-weight: 600; color: var(--color-surface-darker); }
  .sub { font-size: 11px; color: var(--color-text-muted); }
  .download-icon { font-size: 13px; color: #4caf50; vertical-align: middle; margin-left: 2px; }
  .col-act { width: 72px; }
  .col-pick { width: 34px; text-align: center; }
  .col-pick input { margin: 0; accent-color: var(--color-primary); cursor: pointer; }
  .bulk-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; font-size: 13px; }
  .bulk-bar .tool-btn.danger { color: #c62828; border-color: #f1c0c0; }
  .bulk-bar .muted { color: var(--color-text-muted); }
  tr.row-deleted td { opacity: 0.6; }
  .row-actions { display: flex; gap: 2px; justify-content: flex-end; }

  /* Stage pills */
  .stage-pill { display: inline-flex; align-items: center; padding: 2px 9px; border-radius: 10px;
    font-size: 11px; font-weight: 600; white-space: nowrap; }
  .stage-dot { width: 7px; height: 7px; border-radius: 50%; margin-right: 6px;
    background: currentColor; flex-shrink: 0; }
  .stage--open            { background: #e3f2fd; color: #1565c0; }
  .stage--pending_confirmation { background: #fff3e0; color: #e65100; }
  .stage--in_preparation  { background: #f3e5f5; color: #6a1b9a; }
  .stage--ready_for_dispatch { background: #e8f5e9; color: #2e7d32; }
  .stage--received        { background: #e0f2f1; color: #00796b; }
  .stage--confirmed       { background: #e8eaf6; color: #3949ab; }
  .stage--sent_to_picking { background: #fff8e1; color: #ff8f00; }
  .stage--picked          { background: #f1f8e9; color: #558b2f; }
  .stage--documents_prepared { background: #eceff1; color: #455a64; }
  .stage--transport_requested { background: #e0f7fa; color: #0097a7; }
  .stage--transport_confirmed { background: #e0f7fa; color: #00838f; }
  .stage--dispatched      { background: #e0f7fa; color: #00695c; }
  .stage--goods_received  { background: #e8f5e9; color: #1b5e20; }
  .stage--documents_confirmed { background: #ede7f6; color: #5e35b1; }
  .stage--documents_sent_to_accounting { background: #efebe9; color: #6d4c41; }
  .stage--cancelled       { background: #f5f5f5; color: #757575; }
  /* Not one of the stages: a draft has not entered the machine yet. */
  .stage--draft           { background: #eceff1; color: #37474f; }
  .draft-tr td { background: #fbfbfc; }

  .pager { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px;
    font-size: 13px; color: var(--color-text-muted); }

  .btn { display: inline-flex; align-items: center; padding: 8px 16px;
    border-radius: var(--radius); border: none; cursor: pointer; font-size: 13px;
    font-weight: 600; text-decoration: none; }
  .btn--primary { background: var(--color-primary); color: #fff; }
</style>
